import { isChoiceTextResponseInvalid } from './choice-text-response'
import {
  isSurveyNoneOptionId,
  isSurveyOtherOptionId,
  SURVEY_OPTION_NONE_ID,
} from './survey-option-ids'
import type {
  ChoiceDraftAnswer,
  SurveyDraftAnswer,
  SurveyQuestion,
} from './survey-question.types'

export type { ChoiceDraftAnswer } from './survey-question.types'

export function isChoiceDraftAnswer(value: unknown): value is ChoiceDraftAnswer {
  if (value == null || typeof value !== 'object') return false
  const v = value as ChoiceDraftAnswer
  return Array.isArray(v.selectedIds)
}

export function choiceDraftFromIds(
  selectedIds: string[],
  otherText = '',
): ChoiceDraftAnswer {
  return { selectedIds, otherText }
}

const NO_ROUTING_DESTINATIONS: Readonly<Record<string, string>> = {}

export type RoutingDestinationMap = Readonly<Record<string, string>>

/** Checkbox, or Multiple Choice whose min or max selections allow more than one answer. */
export function isMultiSelectSurveyQuestion(question: SurveyQuestion): boolean {
  if (question.type === 'checkbox') return true
  if (question.type !== 'multiple_choice') return false
  return (question.minSelections ?? 0) > 1 || (question.maxSelections ?? 0) > 1
}

export function choiceDraftFromLegacy(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): ChoiceDraftAnswer {
  if (isChoiceDraftAnswer(draft)) return draft

  if (Array.isArray(draft) && (question.type === 'checkbox' || question.type === 'multiple_choice')) {
    return choiceDraftFromIds(draft.filter((x): x is string => typeof x === 'string'))
  }

  if (question.type === 'multiple_choice' || question.type === 'dropdown') {
    const id = typeof draft === 'string' ? draft : ''
    return choiceDraftFromIds(id ? [id] : [])
  }

  return choiceDraftFromIds([])
}

export function routingDestinationsFor(question: SurveyQuestion): RoutingDestinationMap {
  // BFP-251: checkbox is not routing-eligible, so skip/branch rules never limit its selections.
  if (question.type === 'checkbox') return NO_ROUTING_DESTINATIONS
  if (!isMultiSelectSurveyQuestion(question) || question.routingOptionDestinations == null) {
    return NO_ROUTING_DESTINATIONS
  }
  return question.routingOptionDestinations
}

function destinationsConflict(left: string | undefined, right: string | undefined): boolean {
  return left != null && right != null && left !== right
}

/** True when `optionId` follows a different skip/branch than an answer already selected. */
export function optionConflictsWithSelection(
  optionId: string,
  selectedIds: readonly string[],
  destinations: RoutingDestinationMap,
): boolean {
  const dest = destinations[optionId]
  if (!dest) return false
  return selectedIds.some((id) => id !== optionId && destinationsConflict(dest, destinations[id]))
}

/**
 * Drops answers whose skip/branch conflicts with an earlier selected path.
 * Answers with no routing stay selected.
 */
export function normalizeConflictingSelection(
  selectedIds: string[],
  destinations: RoutingDestinationMap,
): string[] {
  if (selectedIds.length < 2) return selectedIds
  let anchor: string | undefined
  const kept: string[] = []
  for (const id of selectedIds) {
    const dest = destinations[id]
    if (!dest) {
      kept.push(id)
      continue
    }
    if (anchor == null) {
      anchor = dest
      kept.push(id)
      continue
    }
    if (dest === anchor) kept.push(id)
  }
  return kept
}

export function selectedIdsFromDraft(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): string[] {
  return choiceDraftFromLegacy(question, draft).selectedIds
}

export function otherTextFromDraft(draft: SurveyDraftAnswer): string {
  if (isChoiceDraftAnswer(draft)) return draft.otherText
  return ''
}

export function isNoneExclusiveSelected(selectedIds: string[]): boolean {
  return selectedIds.length === 1 && isSurveyNoneOptionId(selectedIds[0])
}

export function isOtherSelected(selectedIds: string[]): boolean {
  return selectedIds.some(isSurveyOtherOptionId)
}

export function toggleSingleSelectId(current: string[], optionId: string): string[] {
  if (isSurveyNoneOptionId(optionId)) return [SURVEY_OPTION_NONE_ID]
  if (current.length === 1 && current[0] === optionId) return []
  return [optionId]
}

/**
 * Multi-select toggle. Options that follow different skip/branch destinations cannot be
 * combined; answers with no routing, or the same destination, stay selected together.
 * A conflicting option is left unselected until the other path is cleared.
 */
export function toggleMultiSelectId(
  current: string[],
  optionId: string,
  maxSelections: number | null,
  destinations: RoutingDestinationMap = NO_ROUTING_DESTINATIONS,
): string[] {
  if (isSurveyNoneOptionId(optionId)) {
    const has = current.includes(SURVEY_OPTION_NONE_ID)
    return has ? [] : [SURVEY_OPTION_NONE_ID]
  }
  if (current.includes(optionId)) {
    return current.filter((id) => id !== optionId)
  }
  if (optionConflictsWithSelection(optionId, current, destinations)) {
    return current
  }

  const next = current.filter((id) => !isSurveyNoneOptionId(id))
  if (maxSelections != null && next.length >= maxSelections) {
    return next
  }
  return [...next, optionId]
}

export function isChoiceAnswerEmpty(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): boolean {
  const { selectedIds, otherText } = choiceDraftFromLegacy(question, draft)
  if (selectedIds.length === 0) return true
  return isChoiceTextResponseInvalid(question, selectedIds, otherText)
}

/** BFP-1156 — specify text is required whenever its option is selected, even on optional questions. */
export function isChoiceDraftTextInvalid(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): boolean {
  const { selectedIds, otherText } = choiceDraftFromLegacy(question, draft)
  return isChoiceTextResponseInvalid(question, selectedIds, otherText)
}

export function meetsMinSelections(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): boolean {
  if (!isMultiSelectSurveyQuestion(question) || question.minSelections == null) return true
  const { selectedIds } = choiceDraftFromLegacy(question, draft)
  if (isNoneExclusiveSelected(selectedIds)) return true
  if (loneRoutedSelectionCannotGrow(question, selectedIds)) return true
  return selectedIds.length >= question.minSelections
}

/** A single routed answer meets min when every other option follows a different path. */
function loneRoutedSelectionCannotGrow(
  question: SurveyQuestion,
  selectedIds: string[],
): boolean {
  if (selectedIds.length !== 1) return false
  const destinations = routingDestinationsFor(question)
  const only = selectedIds[0]
  if (!only || destinations[only] == null) return false
  return !question.options.some(
    (option) =>
      option.id !== only && !optionConflictsWithSelection(option.id, selectedIds, destinations),
  )
}
